import { useTranslation } from 'react-i18next'
import { FiBookOpen, FiCloud, FiDollarSign, FiExternalLink, FiAlertCircle } from 'react-icons/fi'
import Card, { CardBody } from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'

export interface KoshaGuide {
  id: string
  title: string
  category?: string | null
  revision?: string | null
  issued_date?: string | null
  summary?: string | null
  source_url?: string | null
  source_attribution?: string | null
}

export interface NewsArticleItem {
  id: string
  accident_id?: string | null
  title: string
  summary?: string | null
  source_url: string
  publisher?: string | null
  published_at?: string | null
}

export interface WeatherAlertItem {
  id: string
  alert_type: string
  severity: string
  title: string
  safety_tip?: string | null
}

export interface SubsidyAlertItem {
  id: string
  title: string
  summary?: string | null
  target?: string | null
  support_amount?: string | null
  apply_end?: string | null
  source_url: string
}

const WEATHER_ICON: Record<string, string> = {
  heat: '🥵', cold: '🥶', rain: '🌧', snow: '❄️', pm10: '😷', pm25: '😷', humidity: '💧',
}

export function WeatherAlertWidget({ alerts }: { alerts: WeatherAlertItem[] }) {
  const { t } = useTranslation()
  if (!alerts.length) return null
  return (
    <Card>
      <CardBody>
        <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
          <FiCloud className="inline mr-1" size={12} />
          {t('dailyBrief.weather.title', { defaultValue: '오늘의 날씨 안전 알림' })}
        </div>
        <div className="space-y-2">
          {alerts.map(a => (
            <div key={a.id} className="flex gap-3 items-start p-2 rounded bg-surface-elevated/40">
              <span className="text-xl flex-none">{WEATHER_ICON[a.alert_type] ?? '⚠'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Badge variant={a.severity === 'severe' ? 'danger' : a.severity === 'warning' ? 'warning' : 'primary'}>
                    {a.severity}
                  </Badge>
                  <span className="text-sm font-semibold text-ink-primary">{a.title}</span>
                </div>
                {a.safety_tip && (
                  <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{a.safety_tip}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}

function _guideKind(revision: string | null | undefined): { ko: string; en: string; color: 'success' | 'warning' | 'primary' } {
  const r = (revision ?? '').toString()
  if (r.includes('제정')) return { ko: '제정', en: 'New', color: 'success' }
  if (r.includes('개정')) return { ko: '개정', en: 'Updated', color: 'warning' }
  if (r.includes('폐지')) return { ko: '폐지', en: 'Removed', color: 'primary' }
  return { ko: '갱신', en: 'Updated', color: 'primary' }
}

function _guideDescription(g: KoshaGuide, lang: 'ko' | 'en'): string {
  // 행정규칙(제2026-XX호 형식)이면 발령번호 + 시행일자
  const r = g.revision ?? ''
  if (r.startsWith('제') && r.includes('호')) {
    return lang === 'ko'
      ? `${g.issued_date ?? ''} 시행, ${r} 공포 (소관: 고용노동부)`
      : `Effective ${g.issued_date ?? ''}, notice ${r}`
  }
  // KOSHA Guide (제정/개정 + 연도)
  if (r.includes('제정') || r.includes('개정')) {
    const kind = r.includes('제정') ? '신규 제정' : '개정'
    return lang === 'ko'
      ? `${kind} 공표 — ${g.category ?? '분야 미상'} 분야의 기술지원규정 (${g.issued_date ?? ''})`
      : `${kind === '신규 제정' ? 'Newly established' : 'Revised'} — ${g.category ?? ''} (${g.issued_date ?? ''})`
  }
  return g.summary ?? ''
}

export function KoshaGuideSection({ guides }: { guides: KoshaGuide[] }) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko'
  if (!guides.length) return null
  return (
    <Card>
      <CardBody>
        <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
          <FiBookOpen className="inline mr-1" size={12} />
          {t('dailyBrief.guide.title', { defaultValue: 'KOSHA Guide / 행정규칙 신규·개정' })}
        </div>
        <ul className="space-y-2">
          {guides.map(g => {
            const kind = _guideKind(g.revision)
            const desc = _guideDescription(g, lang)
            return (
              <li key={g.id} className="border-l-2 border-brand-primary/40 pl-3 py-1.5">
                <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                  <Badge variant={kind.color}>{lang === 'ko' ? kind.ko : kind.en}</Badge>
                  {g.category && <Badge>{g.category}</Badge>}
                  {g.issued_date && <span className="text-[11px] text-ink-faint">· {g.issued_date}</span>}
                </div>
                <div className="text-sm text-ink-primary font-medium">
                  {g.source_url ? (
                    <a href={g.source_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {g.title} <FiExternalLink size={10} className="inline" />
                    </a>
                  ) : g.title}
                </div>
                {desc && (
                  <div className="text-[11px] text-ink-secondary mt-0.5 leading-relaxed">{desc}</div>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

export function NewsArticlesSection({ articles }: { articles: NewsArticleItem[] }) {
  const { t } = useTranslation()
  if (!articles.length) return null
  // 동일 사고에 묶이지 않은 평탄한 리스트로 노출 (간결)
  return (
    <Card>
      <CardBody>
        <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
          📰 {t('dailyBrief.news.title', { defaultValue: '관련 뉴스' })}
        </div>
        <ul className="space-y-2">
          {articles.slice(0, 8).map(n => (
            <li key={n.id} className="text-sm">
              <a href={n.source_url} target="_blank" rel="noopener noreferrer" className="text-ink-primary hover:underline">
                {n.title}
              </a>
              {n.publisher && (
                <span className="ml-2 text-xs text-ink-faint">· {n.publisher}</span>
              )}
              {n.summary && (
                <p className="text-xs text-ink-secondary mt-0.5 leading-relaxed line-clamp-2">{n.summary}</p>
              )}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

export interface LawChangeItem {
  law_name: string
  change_kind: 'updated' | 'new' | 'partial'
  run_date: string
  effective_date?: string | null
  prev_effective_date?: string | null
  category: string
  description: string
  file_count: number
}

const KIND_LABEL: Record<string, { ko: string; en: string; color: 'success' | 'warning' | 'primary' }> = {
  new:     { ko: '신규',   en: 'New',     color: 'success' },
  updated: { ko: '개정',   en: 'Updated', color: 'warning' },
  partial: { ko: '일부 개정', en: 'Partial', color: 'primary' },
}

export function LawChangesSection({ changes }: { changes: LawChangeItem[] }) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko'
  if (!changes.length) return null
  return (
    <Card>
      <CardBody>
        <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
          ⚖ {t('dailyBrief.lawChanges.title', { defaultValue: '법령 개정 알림 (최근 2주)' })}
        </div>
        <ul className="space-y-2">
          {changes.slice(0, 10).map((c, idx) => {
            const meta = KIND_LABEL[c.change_kind] ?? KIND_LABEL.partial
            return (
              <li key={`${c.law_name}-${c.run_date}-${idx}`} className="border-l-2 border-brand-primary/40 pl-3 py-1.5">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <Badge variant={meta.color}>{lang === 'ko' ? meta.ko : meta.en}</Badge>
                  <span className="text-[11px] text-ink-faint">{c.run_date}</span>
                  {c.effective_date && (
                    <span className="text-[11px] text-ink-faint">
                      · {t('dailyBrief.lawChanges.effective', { defaultValue: '시행' })} {c.effective_date}
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium text-ink-primary">{c.law_name}</div>
                {c.description && (
                  <div className="text-[11px] text-ink-secondary mt-0.5 leading-relaxed">
                    {c.description}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

export function SubsidyAlertsSection({ subsidies }: { subsidies: SubsidyAlertItem[] }) {
  const { t } = useTranslation()
  if (!subsidies.length) return null
  return (
    <Card>
      <CardBody>
        <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
          <FiDollarSign className="inline mr-1" size={12} />
          {t('dailyBrief.subsidy.title', { defaultValue: '정부 보조금/지원사업 (마감 임박)' })}
        </div>
        <ul className="space-y-2">
          {subsidies.map(s => (
            <li key={s.id} className="p-2 rounded bg-surface-elevated/40">
              <div className="flex items-start justify-between gap-2">
                <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-ink-primary hover:underline flex-1">
                  {s.title}
                </a>
                {s.apply_end && (
                  <Badge variant="warning">
                    <FiAlertCircle size={10} className="inline mr-0.5" />
                    {s.apply_end}
                  </Badge>
                )}
              </div>
              {s.summary && <p className="text-xs text-ink-secondary mt-1">{s.summary}</p>}
              <div className="flex gap-3 text-[11px] text-ink-faint mt-1">
                {s.target && <span>대상: {s.target}</span>}
                {s.support_amount && <span>지원: {s.support_amount}</span>}
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}
